import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Everything the nudge card needs about the task that was just saved without a date. */
export interface TaskDateNudgeRequest {
    /** The saved task's id — the card writes Start + End back onto it. */
    taskId: number;
    /** The task's name, quoted in the card's second line. */
    taskName: string;
    /** The assignee's display name. The card shows the first word of it. */
    assigneeName: string;
    /**
     * The task was assigned to the person creating it. The card switches to second person — naming
     * yourself ("Nirav can't plan around it") reads as though it went to someone else.
     */
    assigneeIsCurrentUser: boolean;
}

/**
 * Owns the "Forgot a date?" nudge — the dark card that drops in after a task is saved with an assignee
 * but no Start/End.
 *
 * Feature-scoped on purpose: `STANDARDS.md` §1.12 bans new channels on the `DataService` bus, so the
 * one producer (the create-task modal) and the one consumer (the card hosted by `MainLayoutComponent`,
 * §1.13) talk through this typed service instead.
 *
 * It also holds the two pieces of state that must outlive any single card:
 *  - the ids already nudged, so a task is never nudged twice ("never re-open for the same task");
 *  - the dated-in-a-row streak, reset when the company work date rolls over so "1 dated today" is true.
 */
@Injectable({ providedIn: 'root' })
export class TaskDateNudgeService {

    private readonly _requests = new Subject<TaskDateNudgeRequest>();
    /** A task was just saved undated-but-assigned; the host should raise the card. */
    readonly requests$: Observable<TaskDateNudgeRequest> = this._requests.asObservable();

    /** Tasks already nudged this session — the card is a one-shot per task, on create only. */
    private readonly nudgedTaskIds = new Set<number>();

    private streakCount = 0;
    /** The `YYYY-MM-DD` company work date the streak belongs to; a different day starts a new one. */
    private streakDay = '';

    /**
     * Ask for the card. Ignored when this task has already been nudged, so a re-save or a second
     * create callback for the same id can't raise it again.
     */
    request(request: TaskDateNudgeRequest): void {
        if (!request || !request.taskId || this.nudgedTaskIds.has(request.taskId)) {
            return;
        }
        this.nudgedTaskIds.add(request.taskId);
        this._requests.next(request);
    }

    /** How many tasks have been dated from the card on `today` (`YYYY-MM-DD`, company tz). */
    streak(today: string): number {
        return this.streakDay === today ? this.streakCount : 0;
    }

    /** Count one dated task and return the new streak. */
    recordDated(today: string): number {
        this.streakCount = this.streakDay === today ? this.streakCount + 1 : 1;
        this.streakDay = today;
        return this.streakCount;
    }
}
